<!--
SPDX-FileCopyrightText: 2023 Marlon W (Mawoka)
SPDX-FileCopyrightText: 2026 Max Booth

SPDX-License-Identifier: MPL-2.0
-->

<script lang="ts">
	import { getLocalization } from '$lib/i18n';
	import { signedIn, pathname } from '$lib/stores';
	import { createTippy } from 'svelte-tippy';
	import { browser } from '$app/environment';
	import { beforeNavigate } from '$app/navigation';
	import { draw, slide } from 'svelte/transition';
	import { registration_disabled, site_name } from './config';
	import Logo from '$lib/components/logo.svelte';

	const tippy = createTippy({
		arrow: true,
		animation: 'perspective-subtle',
		placement: 'bottom'
	});

	const { t } = getLocalization();

	let menuIsClosed = $state(true);
	const toggleMenu = () => {
		menuIsClosed = !menuIsClosed;
	};

	beforeNavigate(() => {
		menuIsClosed = true; // Closes menu to let the user see the page beneath
	});

	let darkMode = $state(false);
	if (browser) {
		darkMode =
			localStorage.theme === 'dark' ||
			(!('theme' in localStorage) &&
				window.matchMedia('(prefers-color-scheme: dark)').matches);
	}

	const switchDarkMode = () => {
		!darkMode ? localStorage.setItem('theme', 'dark') : localStorage.setItem('theme', 'light');
		window.location.reload();
	};
</script>

{#snippet themeToggle()}
	<button
		class="p-2 rounded-full text-slate-600 dark:text-slate-300 hover:bg-indigo-100 dark:hover:bg-white/10 transition"
		onclick={switchDarkMode}
		use:tippy={{ content: darkMode ? 'Switch light mode on' : 'Switch dark mode on' }}
		aria-label={darkMode ? 'Activate light mode' : 'Activate dark mode'}
	>
		{#if darkMode}
			<!-- Heroicons: sun -->
			<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
				<path
					stroke-linecap="round"
					stroke-linejoin="round"
					stroke-width="2"
					d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
				/>
			</svg>
		{:else}
			<!-- Heroicons: moon -->
			<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
				<path
					stroke-linecap="round"
					stroke-linejoin="round"
					stroke-width="2"
					d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
				/>
			</svg>
		{/if}
	</button>
{/snippet}

{#snippet brand()}
	<a href="/" class="flex items-center gap-2 pr-3 group" aria-label="{site_name} home">
		<Logo class="w-9 h-9 transition-transform group-hover:-rotate-6" />
		<span class="font-display text-xl font-semibold tracking-tight text-slate-900 dark:text-white"
			>{site_name}</span
		>
	</a>
{/snippet}

<nav
	class="w-screen px-4 lg:px-10 py-2.5 fixed top-0 z-30 backdrop-blur-xl bg-white/75 dark:bg-[#161331]/80 border-b border-indigo-100/70 dark:border-white/10"
>
	<!-- Desktop navbar -->
	<div class="hidden lg:flex lg:items-center lg:justify-between max-w-7xl mx-auto">
		<div class="flex items-center gap-1">
			{@render brand()}
			<a class="btn-nav" href="/explore">{$t('words.explore')}</a>
			<a class="btn-nav" href="/search">{$t('words.search')}</a>
			{#if $signedIn}
				<a class="btn-nav" href="/dashboard">{$t('words.dashboard')}</a>
			{:else}
				<a class="btn-nav" href="/docs">{$t('words.docs')}</a>
			{/if}
		</div>
		<div class="flex items-center gap-2">
			{#if $signedIn}
				<a class="btn-nav" href="/api/v1/users/logout">{$t('words.logout')}</a>
			{:else}
				<a class="btn-nav" href="/account/login?returnTo={$pathname}">{$t('words.login')}</a>
				{#if !registration_disabled}
					<a class="btn-nav" href="/account/register">{$t('words.register')}</a>
				{/if}
			{/if}
			{@render themeToggle()}
			<a class="btn-primary ml-1" href="/play">{$t('words.play')}</a>
		</div>
	</div>

	<!-- Mobile navbar -->
	<div class="lg:hidden">
		<div class="flex items-center justify-between">
			{@render brand()}
			<div class="flex items-center gap-1">
				<a class="btn-primary !py-1.5 !px-4" href="/play">{$t('words.play')}</a>
				{@render themeToggle()}
				<button
					class="p-2 rounded-full text-slate-700 dark:text-slate-200"
					onclick={toggleMenu}
					aria-label={menuIsClosed ? 'Open navbar' : 'Close navbar'}
					aria-expanded={!menuIsClosed}
				>
					<svg
						width="24"
						height="24"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						stroke-width="2"
						stroke-linecap="round"
						stroke-linejoin="round"
						aria-hidden="true"
					>
						{#if menuIsClosed}
							<path d="M3 6h18M3 12h18M3 18h18" />
						{:else}
							<path in:draw|global={{ duration: 300 }} d="M18 6 6 18" />
							<path in:draw|global={{ duration: 300 }} d="m6 6 12 12" />
						{/if}
					</svg>
				</button>
			</div>
		</div>

		{#if !menuIsClosed}
			<div class="flex flex-col py-2" transition:slide|global={{ duration: 300 }}>
				<a class="btn-nav" href="/explore">{$t('words.explore')}</a>
				<a class="btn-nav" href="/search">{$t('words.search')}</a>
				{#if $signedIn}
					<a class="btn-nav" href="/dashboard">{$t('words.dashboard')}</a>
				{:else}
					<a class="btn-nav" href="/docs">{$t('words.docs')}</a>
				{/if}
				<hr class="my-2 border-indigo-100 dark:border-white/10" />
				{#if $signedIn}
					<a class="btn-nav" href="/api/v1/users/logout">{$t('words.logout')}</a>
				{:else}
					<a class="btn-nav" href="/account/login?returnTo={$pathname}">{$t('words.login')}</a>
					{#if !registration_disabled}
						<a class="btn-nav" href="/account/register">{$t('words.register')}</a>
					{/if}
				{/if}
			</div>
		{/if}
	</div>
</nav>
